import * as React from 'react';
import {
    AlertDialog,
    AlertDialogContent,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogAction,
    AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export interface ConfirmOptions {
    title: string;
    description?: React.ReactNode;
    /** default 'Confirm' */
    confirmLabel?: string;
    /** default 'Cancel' */
    cancelLabel?: string;
    /** default 'default' */
    variant?: 'default' | 'destructive';
    /** When set, the user must type this exact string to enable the confirm button. */
    requireTyped?: string;
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

interface ConfirmState {
    options: ConfirmOptions;
    resolve: (value: boolean) => void;
}

const ConfirmContext = React.createContext<ConfirmFn | null>(null);

let hasWarnedNoProvider = false;

function fallbackConfirm(options: ConfirmOptions): Promise<boolean> {
    if (!hasWarnedNoProvider) {
        hasWarnedNoProvider = true;
        // eslint-disable-next-line no-console
        console.warn(
            '[useConfirm] No <ConfirmProvider> found in the tree. Falling back to window.confirm.'
        );
    }
    const message = options.description
        ? `${options.title}\n\n${typeof options.description === 'string' ? options.description : ''}`
        : options.title;
    return Promise.resolve(window.confirm(message));
}

export const ConfirmProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [state, setState] = React.useState<ConfirmState | null>(null);
    const [typedValue, setTypedValue] = React.useState('');
    const resolvedRef = React.useRef(false);

    const confirm = React.useCallback<ConfirmFn>(options => {
        return new Promise<boolean>(resolve => {
            resolvedRef.current = false;
            setTypedValue('');
            setState({ options, resolve });
        });
    }, []);

    const settle = React.useCallback((value: boolean) => {
        if (resolvedRef.current) return;
        resolvedRef.current = true;
        setState(current => {
            current?.resolve(value);
            return null;
        });
        setTypedValue('');
    }, []);

    const handleOpenChange = React.useCallback(
        (open: boolean) => {
            if (!open) {
                settle(false);
            }
        },
        [settle]
    );

    const options = state?.options;
    const requireTyped = options?.requireTyped;
    const isTypedMismatch = Boolean(requireTyped) && typedValue.trim() !== requireTyped?.trim();

    return (
        <ConfirmContext.Provider value={confirm}>
            {children}
            <AlertDialog open={state !== null} onOpenChange={handleOpenChange}>
                {options && (
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle>{options.title}</AlertDialogTitle>
                            {options.description && (
                                <AlertDialogDescription>
                                    {options.description}
                                </AlertDialogDescription>
                            )}
                        </AlertDialogHeader>
                        {requireTyped && (
                            <div className="space-y-2 py-2">
                                <Label htmlFor="confirm-typed-input">
                                    Type <span className="font-semibold">{requireTyped}</span> to
                                    confirm
                                </Label>
                                <Input
                                    id="confirm-typed-input"
                                    autoComplete="off"
                                    value={typedValue}
                                    onChange={event => setTypedValue(event.target.value)}
                                />
                            </div>
                        )}
                        <AlertDialogFooter>
                            <AlertDialogCancel onClick={() => settle(false)}>
                                {options.cancelLabel ?? 'Cancel'}
                            </AlertDialogCancel>
                            <AlertDialogAction
                                variant={
                                    options.variant === 'destructive' ? 'destructive' : 'default'
                                }
                                disabled={isTypedMismatch}
                                onClick={() => settle(true)}
                            >
                                {options.confirmLabel ?? 'Confirm'}
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                )}
            </AlertDialog>
        </ConfirmContext.Provider>
    );
};

export function useConfirm(): ConfirmFn {
    const context = React.useContext(ConfirmContext);
    if (!context) {
        return fallbackConfirm;
    }
    return context;
}
